<script lang="ts" module>
  import type { NavbarStore } from '@agapi/ui/navbar';
  import type { ProductVariant } from '@agapi/mlc-core';
  import type { CartStore, StoreCartItem } from '../../stores/cart';

  export interface MiniCartProps {}
</script>

<script lang="ts">
  import { page } from '$app/state';

  import { getContext, untrack } from 'svelte';
  import { ShoppingCart } from 'phosphor-svelte';

  import { css } from '@agapi/ui/panda/css';
  import { flex, stack, icon } from '@agapi/ui/panda/patterns';

  import { Badge } from '@agapi/ui/badge';
  import { Button } from '@agapi/ui/button';

  import FreeShippingProgress from './_components/minicart-freeshipping-progress.svelte';
  import * as MiniCart from './_components';
  import { ProductCard, CartItem, CartItemList } from '../../components';

  import { EVENTS, getCommerce } from '../../services';
  import { getCookie } from '../../utils/cookies.client';
  import { rewriteUrl, getLocaleFromUrl } from '../../utils/locale.shared';

  import * as m from '$lib/parachute/messages';

  const COMMERCE = getCommerce(window.fetch);

  const cart = getContext<CartStore>('cart-store');
  const navbarStore = getContext<NavbarStore>('navbar');

  let recommendationsLimit = $state(6);
  let recommendations: ProductVariant[] = $state([]);

  $effect(() => {
    if (recommendations.length < recommendationsLimit) {
      untrack(async () => {
        if (cart.items.length > 0) { // Put this here as we do not want to track on cart.items
          const limit = Math.max(recommendationsLimit - recommendations.length, 0);
          const identifier = getCookie<string>('__agapi_identifier') ?? 'default';

          // TODO: use this when loop54 test environment has more data trained
          const basketRecommendations = await COMMERCE.getRecommendationDerivedFrom(cart.items.map(item => item.sku), { limit }, getLocaleFromUrl(page.url), { userId: identifier });
          // const basketRecommendations = await COMMERCE.getRecommendation({ limit }, getLocaleFromUrl(page.url), { userId: identifier });
          recommendationsLimit = basketRecommendations.items.length; // If we get less than 10 recommendations, we will not fetch more
          console.log('basketRecommendations', limit, basketRecommendations);

          // Only add items that don't already exist in recommendations
          recommendations.push(...basketRecommendations.items.filter(i => !recommendations.some(e => e.sku === i.sku)));
        }
      });
    }
  });

  const onInput = async (item: StoreCartItem, quantity: number) => {
    console.log('adding item', item, quantity);
    if (Number.isInteger(quantity) && quantity > 0) {
      try {
        const quantityChange = quantity - item.quantity;
        console.log('quantityChange', quantityChange);

        console.log('handleMiniCartUpdate', { id: item.id, quantity }, quantityChange, Math.abs(quantityChange));
        await cart.update(item, quantity);

        // May throw error and catched below and is being ignored now, do anything?
        EVENTS.emit(quantityChange > 0 ? 'cart_item_add' : 'cart_item_remove', { variant: item, quantity: Math.abs(quantityChange), ignoreDrawer: true });
      } catch (error: any) {
        console.error(error);
        if (Number.isInteger(error?.stock)) {
          item.stock = error.stock; // Update so the quantity selector gets correct amount stock
        }
      }
    }
  };

  const onRemove = (item: StoreCartItem) => {
    cart.remove(item);
    EVENTS.emit('cart_item_remove', { variant: item, quantity: item.quantity });
  };

  const cartItemsSectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflowY: 'scroll',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
  });
</script>

<MiniCart.Root css={{ zIndex: 20 }}>
  {#snippet header()}
    {@const totalQuantity = cart.items.reduce((acc, item) => acc + item.quantity, 0)}
    <MiniCart.Header css={{ flex: 0 }} onclose={() => navbarStore.closeAll()}>
      <h4 class={css({ textStyle: 'h5' })}>
        {m.miniCartTitle('miniCartTitle@@Min varukorg')}
      </h4>
      <Badge size="md" outlined colorVariant="secondary.subdued">
        {totalQuantity === 1 ? m.waresSingular({ value: totalQuantity }) : m.waresPlural({ value: totalQuantity })}
      </Badge>
    </MiniCart.Header>
  {/snippet}

  {#snippet content()}
    <section class={cartItemsSectionStyles}>
      <CartItemList css={{ pb: 4 }}>
        {#if cart.items.length}
          {#each cart.items as item (item.id)}
            <CartItem {item} onInput={(_sku, quantity) => onInput(item, quantity)} onRemove={(_sku) => onRemove(item)} clickable={true} />
          {/each}
        {:else}
          <li>
            <span>{m.emptyCartText('emptyCartText@@Din varukorg är tom.')}</span>
          </li>
        {/if}
      </CartItemList>
      {#if cart.items.length}
      <a href={rewriteUrl(page, '/checkout')} class={css({ display: 'flex', width: 'calc(100% - (var(--spacing-4) * 2))', mx: 4, pb: '5', justifyContent: 'center' })}>
        <Button variant="cta" size="sm" onclick={() => navbarStore.closeAll()}>
          {m.startCheckout()}
          <ShoppingCart>
            <title>{m.iconShoppingCart()}</title>
          </ShoppingCart>
        </Button>
      </a>
      {/if}

      {#if recommendations.length > 0}
        <div class={stack({ px: 4, py: 7 })}>
          <h5 class={css({ textStyle: 'h6' })}>
            {m.recommendedForYou()}
          </h5>
          <div class={css({ gap: '4', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' })}>
            {#each recommendations as variant (variant.sku)}
              <ProductCard.Medium
                product={variant}
                buttonLabel={m.buy()}
                css={{ minWidth: 'unset' }}
                onProductAdded={() => {
                  recommendations.splice(recommendations.findIndex((i) => i.sku === variant.sku), 1);
                }}
                onclick={() => {
                  navbarStore.closeAll();
                }}
              />
            {/each}
          </div>
        </div>
      {/if}
    </section>
  {/snippet}

  {#snippet footer()}
    <MiniCart.Footer css={{ flex: 0 }}>
      <FreeShippingProgress progress={Math.min((cart.summary.raw.finalAmount / cart.summary.raw.freeShippingLimit) * 100, 100)}>
        <div class={stack({ flexDirection: 'row', alignItems: 'flex-end' })}>
          {#if cart.summary.raw.finalAmount < cart.summary.raw.freeShippingLimit}
            <span class={css({ fontSize: 'xl', color: 'surface.primary.vibrant.foreground', lineHeight: 'tight' })}>
              {cart.summary.formatted.amountUntilFreeShipping}
            </span>
          {/if}

          {#if cart.summary.raw.finalAmount >= cart.summary.raw.freeShippingLimit}
            <span class={css({ fontSize: 'lg', fontWeight: 'bold', color: 'surface.primary.vibrant.foreground', pl: '2' })}>
              {m.reachedFreeShipping()}
            </span>
          {:else}
            <span class={css({ fontSize: 'md', color: 'surface.primary.vibrant.foreground', pl: '2' })}>
              {m.untilFreeShipping()}
            </span>
          {/if}
        </div>
      </FreeShippingProgress>

      <div class={flex({ justify: 'space-between' })}>
        <span class={css({ lineHeight: 'none' })}>
          {m.deliveryStartsAt()}
        </span>
        <span class={css({ lineHeight: 'none' })}>
          {cart.summary.formatted.shippingCost}
        </span>
      </div>

      {#if cart.discountCode}
        <div class={flex({ justify: 'space-between', fontSize: 'sm' })}>
          <span class={css({ lineHeight: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' })}>
            {m.discountCodeApplied({ code: '' })}
            <b>{cart.discountCode.code}</b>
          </span>
          <span class={css({ lineHeight: 'none' })}>
            {cart.summary.formatted.couponDiscount}
          </span>
        </div>
      {/if}

      <div class={flex({ justify: 'space-between' })}>
        <span class={css({ fontWeight: 'bold', fontSize: 'lg', lineHeight: 'none' })}>
          {m.totalPriceIncVat()}
        </span>
        <span class={css({ fontWeight: 'bold', fontSize: 'lg', lineHeight: 'none' })}>
          {cart.summary.formatted.finalAmount}
        </span>
      </div>

      <a href={rewriteUrl(page, '/checkout')} class={css({ display: 'flex', width: '100%' })}>
        <Button variant="cta" fullWidth onclick={() => navbarStore.closeAll()}>
          {m.startCheckout()}
          <ShoppingCart class={icon({ size: 'md' })}>
            <title>{m.iconShoppingCart()}</title>
          </ShoppingCart>
        </Button>
      </a>
    </MiniCart.Footer>
  {/snippet}
</MiniCart.Root>
